<script setup lang="ts">
const { t } = useTexts();
const { openToaster } = useLayoutStore();

const {
  currentReport,
  isGenerating,
  isPolling,
  error,
  availableYears,
  generateIncomeReport,
  stopPolling,
  fetchAvailableYears,
} = useIncomeReport();

const selectedYear = ref(String(new Date().getFullYear()));

const isDownloadLoading = ref(false);
const isEmailLoading = ref(false);
const isLoadingYears = ref(true);
const accessDeniedMessage = ref<string | null>(null);

useSeo({
  title: t('income_report.title'),
});

const yearOptions = computed(() => {
  return availableYears.value.map((year) => ({
    value: String(year),
    label: String(year),
  }));
});

async function handleDownload() {
  if (!selectedYear.value) return;

  isDownloadLoading.value = true;

  const year = Number(selectedYear.value);

  if (!year) {
    isDownloadLoading.value = false;

    return;
  }

  const result = await generateIncomeReport(year, 'download');

  if (result.success) {
    openToaster({
      message: t('income_report.user.generating_message'),
      type: 'success',
      time: 5000,
    });
  } else {
    isDownloadLoading.value = false;

    if (result.error === 'type_not_allowed') {
      openToaster({
        message: t('income_report.errors.type_not_allowed'),
        type: 'error',
      });
    } else if (result.error === 'already_processing') {
      openToaster({
        message: t('income_report.errors.already_processing'),
        type: 'error',
      });
    } else {
      openToaster({
        message: error.value || t('income_report.errors.generate_error'),
        type: 'error',
      });
    }
  }
}

async function handleSendEmail() {
  if (!selectedYear.value) return;

  isEmailLoading.value = true;

  const year = Number(selectedYear.value);

  if (!year) {
    isEmailLoading.value = false;

    return;
  }

  const result = await generateIncomeReport(year, 'email');

  isEmailLoading.value = false;

  if (result.success) {
    openToaster({
      message: t('income_report.user.email_success'),
      type: 'success',
    });
  } else if (result.error === 'type_not_allowed') {
    openToaster({
      message: t('income_report.errors.type_not_allowed'),
      type: 'error',
    });
  } else if (result.error === 'already_processing') {
    openToaster({
      message: t('income_report.errors.already_processing'),
      type: 'error',
    });
  } else {
    openToaster({
      message: error.value || t('income_report.errors.generate_error'),
      type: 'error',
    });
  }
}

watch(currentReport, (report) => {
  if (report && report.status === 3 && report.delivery_type === 'download') {
    isDownloadLoading.value = false;

    openToaster({
      message: t('income_report.user.download_success'),
      type: 'success',
    });

    setTimeout(() => {
      if (report.download_url) {
        window.open(report.download_url, '_blank');
      }
    }, 1000);
  }

  if (report && report.status === 4) {
    isDownloadLoading.value = false;

    openToaster({
      message: t('income_report.errors.processing_failed'),
      type: 'error',
    });
  }
});

onMounted(async () => {
  isLoadingYears.value = true;

  const result = await fetchAvailableYears();

  if (result.success && availableYears.value.length > 0) {
    selectedYear.value = String(availableYears.value[0]);
  } else if (!result.success && result.message) {
    accessDeniedMessage.value = result.message;
  }

  isLoadingYears.value = false;
});

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <UserAccountPageLayout
    on-back="user-account"
    :title="$t('income_report.title')"
  >
    <UserBox>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <h2 class="text-base font-semibold text-texts">
            {{ $t('income_report.user.title') }}
          </h2>
          <p class="text-sm text-texts/70">
            {{ $t('income_report.user.description_1') }}
          </p>

          <p class="text-sm text-texts/70">
            {{ $t('income_report.user.description_2') }}
          </p>

          <p class="text-sm text-texts/70">
            {{ $t('income_report.user.description_3') }}
          </p>
        </div>

        <!-- Mensagem de acesso negado -->
        <div
          v-if="accessDeniedMessage"
          class="flex flex-col gap-3 w-full max-w-[600px] p-4 rounded-md bg-error/10 border border-error/20"
        >
          <p class="text-sm text-error font-medium">
            {{ accessDeniedMessage }}
          </p>
        </div>

        <!-- Formulário normal -->
        <div
          v-else-if="!isLoadingYears"
          class="flex flex-col gap-3 w-full max-w-[400px]"
        >
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-texts">
              {{ $t('income_report.user.select_year') }}
            </label>
            <BaseFormSelectField
              v-model="selectedYear"
              :disabled="isGenerating || isPolling"
              :initial-value="selectedYear"
              :options="yearOptions"
            />
          </div>

          <div class="flex flex-wrap gap-2 w-full">
            <button
              class="py-2 px-4 rounded-md flex-1 min-w-28 text-xs md:text-sm flex items-center justify-center gap-2 bg-primary text-button-text font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="
                !selectedYear ||
                isDownloadLoading ||
                isEmailLoading ||
                isPolling ||
                isLoadingYears
              "
              @click="handleDownload"
            >
              <NuxtIcon
                v-if="isDownloadLoading || isPolling"
                :class="$style.spinner"
                filled
                name="loading"
              />
              {{ $t('income_report.user.download_button') }}
            </button>
            <button
              class="py-2 px-4 rounded-md flex-1 min-w-28 text-xs md:text-sm flex items-center justify-center gap-2 bg-white text-bg-primary font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="
                !selectedYear ||
                isDownloadLoading ||
                isEmailLoading ||
                isPolling ||
                isLoadingYears
              "
              @click="handleSendEmail"
            >
              <NuxtIcon
                v-if="isEmailLoading"
                :class="$style.spinner"
                filled
                name="loading"
              />
              {{ $t('income_report.user.email_button') }}
            </button>
          </div>

          <p
            v-if="isPolling"
            class="text-xs text-texts/70 text-center"
          >
            {{ $t('income_report.user.generating_message') }}
          </p>
        </div>
        <!-- Fim do formulário -->

        <div class="flex flex-col gap-3 pt-4 border-t border-texts/10">
          <h3 class="text-sm md:text-base font-semibold text-texts">
            {{ $t('income_report.user.faq_title') }}
          </h3>
          <div class="flex flex-col">
            <WidgetsAccordion
              id="faq-irpf-1"
              :title="$t('income_report.user.faq.question_1.title')"
            >
              <span class="text-2xs md:text-xs text-texts/70 -mt-1 mb-4 block">
                {{ $t('income_report.user.faq.question_1.answer') }}
              </span>
            </WidgetsAccordion>
            <WidgetsAccordion
              id="faq-irpf-2"
              :title="$t('income_report.user.faq.question_2.title')"
            >
              <span class="text-2xs md:text-xs text-texts/70 -mt-1 mb-4 block">
                {{ $t('income_report.user.faq.question_2.answer') }}
              </span>
            </WidgetsAccordion>
            <WidgetsAccordion
              id="faq-irpf-3"
              :title="$t('income_report.user.faq.question_3.title')"
            >
              <span class="text-2xs md:text-xs text-texts/70 -mt-1 mb-4 block">
                {{ $t('income_report.user.faq.question_3.answer') }}
              </span>
            </WidgetsAccordion>
            <WidgetsAccordion
              id="faq-irpf-4"
              :title="$t('income_report.user.faq.question_4.title')"
            >
              <span class="text-2xs md:text-xs text-texts/70 -mt-1 mb-4 block">
                {{ $t('income_report.user.faq.question_4.answer') }}
              </span>
            </WidgetsAccordion>
            <WidgetsAccordion
              id="faq-irpf-5"
              :title="$t('income_report.user.faq.question_5.title')"
            >
              <span class="text-2xs md:text-xs text-texts/70 -mt-1 mb-4 block">
                {{ $t('income_report.user.faq.question_5.answer') }}
              </span>
            </WidgetsAccordion>
          </div>
        </div>
      </div>
    </UserBox>
  </UserAccountPageLayout>
</template>

<style module lang="scss">
.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
